import Dexie, { Table } from 'dexie';

import { Issue, Article, Style } from '@/constants';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('Db');

// Exported so the useDb-failure recovery screen can delete exactly this
// database (indexedDB.deleteDatabase(DEXIE_DB_NAME)) without duplicating
// the literal and risking drift.
export const DEXIE_DB_NAME = 'canvasflow';

export class DexieDb extends Dexie implements Db {
	issues!: Table<Issue>;
	articles!: Table<Article>;
	styles!: Table<Style>;
	constructor() {
		super(DEXIE_DB_NAME);
		this.version(1).stores({
			issues: '&id, index, slug',
			articles: '&id, index, IssueID, slug, isFeatured',
			styles: '&id',
		});
	}

	// Dexie doesn't eagerly connect on `new Dexie()` -- it configures the
	// schema and connects lazily on the first real table operation, or
	// when `.open()` is called explicitly. This used to just be
	// `Promise.resolve()`, a no-op that resolved regardless of whether the
	// real database ever opened -- which meant a genuine open failure
	// (a version conflict, quota exhaustion, private-browsing restrictions)
	// was never caught here at all; it would only have surfaced much later,
	// at whatever random point something first touched a table, with
	// nothing awaiting it. `useDb`'s failure path (and the recovery screen
	// it drives) depends on this actually reflecting whether the database
	// opened -- confirmed by forcing a real Dexie VersionError in manual
	// testing, which this fix is what made catchable at all.
	async start(): Promise<void> {
		await this.open();
	}

	async saveIssues(issues: Array<Issue>): Promise<Array<Issue>> {
		for (let i = 0; i < issues.length; i++) {
			const issue = issues[i];
			issue.index = i;
			await this.saveIssue(issue);
		}
		return this.getIssues();
	}

	async getIssues(): Promise<Array<Issue>> {
		const issues = await this.issues.toArray();
		return issues.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
	}

	async getIssue(id: string, isSlug?: boolean): Promise<Issue | null> {
		const cachedIssue = isSlug
			? await this.issues.get({ slug: id })
			: await this.issues.get({ id });
		if (!cachedIssue) {
			return null;
		}
		return cachedIssue;
	}

	async saveIssue(issue: Issue): Promise<Issue | null> {
		if (issue.articles) {
			await this.deleteArticlesByIssue(issue.id);
			issue.articles = issue.articles.map((article: Article) => {
				article.IssueID = `${issue.id}`;
				return article;
			});
			await this.saveArticles(clone(issue.articles));
			delete issue.articles;
		}
		await this.issues.put(issue, 'id');
		const cachedIssue = await this.getIssue(issue.slug ?? issue.id, true);
		if (cachedIssue) {
			cachedIssue.articles = await this.getArticlesByIssue(
				cachedIssue.id,
			);
		}
		return cachedIssue;
	}

	async deleteArticlesByIssue(id: string): Promise<void> {
		await this.articles.where('IssueID').equals(id).delete();
	}

	async deleteArticle(id: string): Promise<void> {
		await this.articles.where('id').equals(id).delete();
	}

	async saveArticles(articles: Array<Article>): Promise<void> {
		logger.debug(`ARTICLES`, articles);
		await this.articles.bulkPut(articles);
	}

	async saveFeatureArticles(articles: Array<Article>): Promise<void> {
		logger.debug(`FEATURED ARTICLES`, articles);
		/* Remove existing feature articles in the issue */
		const issuesSet = new Set<string>([]);
		for (const article of articles) {
			if (article.IssueID) {
				issuesSet.add(`${article.IssueID}`);
			}
		}
		await this.removeFeatureArticleInIssue([...issuesSet]);
		/**************************************************/

		const featureArticles = await this.getFeatureArticles();
		const featureArticlesMap = new Map<string, Article>();
		for (const article of featureArticles) {
			featureArticlesMap.set(article.id, article);
		}
		const response = [];
		for (const article of articles) {
			const { featureImage, name, slug, thumbnail } = article;
			if (featureArticlesMap.has(article.id)) {
				const cachedArticle = featureArticlesMap.get(article.id);
				if (!cachedArticle) {
					continue;
				}
				cachedArticle.featureImage = featureImage;
				cachedArticle.name = name;
				cachedArticle.slug = slug;
				cachedArticle.thumbnail = thumbnail;
				response.push(cachedArticle);
			} else {
				response.push(article);
			}
		}
		await this.articles.bulkPut(response);
	}

	async removeFeatureArticleInIssue(issues: Array<string>) {
		for (const issue of issues) {
			await this.articles
				.where('IssueID')
				.equals(`${issue}`)
				.filter((a: Article) => !!a.isFeatured)
				.delete();
		}
	}

	async getArticlesByIssue(id: string): Promise<Array<Article>> {
		return this.articles.where('IssueID').equals(`${id}`).toArray();
	}

	async getFeatureArticles(): Promise<Article[]> {
		return await this.articles.filter((a) => !!a.isFeatured).toArray();
	}

	async getArticle(id: string, isSlug?: boolean): Promise<Article | null> {
		const cachedArticle = isSlug
			? await this.articles.get({ slug: id })
			: await this.articles.get({ id });
		if (!cachedArticle) {
			return null;
		}
		return cachedArticle;
	}

	async removeStyles(): Promise<void> {
		await this.styles.clear();
	}

	// Additive -- upserts by id, never clears the table first. Called on
	// every issue load (`pages/issue/model/issue.hooks.ts`'s `useIssue`) so
	// that any article's style is available in Dexie the moment a reader
	// might favorite it; a destructive clear-then-replace here would evict
	// the style any *other* already-favorited article (from a different
	// issue) depends on every time a new issue loads. Use `removeStyles()`
	// for an actual wholesale wipe.
	async saveStyles(styles: Array<Style>): Promise<void> {
		// `Style.id` is `string` on the wire but arrives as a number often
		// enough to matter (every *read* here already stringifies it:
		// `getStyle`'s `{ id }`, `deleteStyles`'s own map). `id` is this
		// table's primary key (`styles: '&id'`), and IndexedDB key
		// comparison is type-sensitive -- a style put with a numeric id is
		// stored under a numeric key that no later string lookup or delete
		// can ever match. Normalizing here, at the one write path, is what
		// keeps every read/delete elsewhere actually able to find it.
		styles = uniqueBy(
			styles.map((style) => ({ ...style, id: `${style.id}` })),
			'id',
		);
		logger.debug(`STYLES`, styles);
		await this.styles.bulkPut(styles);
	}

	async getStyles(): Promise<Style[]> {
		return await this.styles.toArray();
	}

	// Targeted counterpart to `removeStyles()`'s wholesale clear, for the
	// favorites flow's reference-counted cleanup (`entities/favorite`'s
	// `pruneUnusedFavoriteStyles`): a style is only ever removed once no
	// favorite article still references it. Ids are stringified for the same
	// reason every other style lookup here is -- they're `string` on the wire
	// but arrive as numbers often enough to matter.
	async deleteStyles(ids: Array<string | number>): Promise<void> {
		if (!ids.length) {
			return;
		}
		await this.styles.bulkDelete(ids.map((id) => `${id}`));
	}

	async getStyle(id: string): Promise<Style | null> {
		const style = await this.styles.get({ id });
		if (!style) {
			return null;
		}
		return style;
	}

	async clear(): Promise<void> {
		this.issues.clear();
		this.articles.clear();
		this.styles.clear();
	}
}

export interface Db {
	start(): Promise<void>;
	clear(): Promise<void>;
	saveIssues(issues: Array<Issue>): Promise<Array<Issue>>;
	getIssues(): Promise<Array<Issue>>;
	getIssue(id: string, isSlug?: boolean): Promise<Issue | null>;
	saveIssue(issue: Issue): Promise<Issue | null>;
	deleteArticlesByIssue(id: string): Promise<void>;
	deleteArticle(id: string): Promise<void>;
	saveArticles(articles: Array<Article>): Promise<void>;
	saveFeatureArticles(articles: Array<Article>): Promise<void>;
	getFeatureArticles(): Promise<Array<Article>>;
	getArticlesByIssue(id: string): Promise<Array<Article>>;
	getArticle(id: string, isSlug?: boolean): Promise<Article | null>;
	removeStyles(): Promise<void>;
	saveStyles(styles: Array<Style>): Promise<void>;
	getStyles(): Promise<Array<Style>>;
	getStyle(id: string): Promise<Style | null>;
	deleteStyles(ids: Array<string | number>): Promise<void>;
}

export function uniqueBy<T>(inputs: Array<T>, property: keyof T): Array<T> {
	const response: Array<T> = [];
	const mapResponse: Record<string, T> = {};
	for (const input of inputs) {
		mapResponse[`${String(input[property])}`] = input;
	}

	for (const input in mapResponse) {
		response.push(mapResponse[input]);
	}

	return response;
}

function clone<T>(obj: T): T {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
